/**
 * Shared SEO builder for service detail pages.
 *
 * Produces server-rendered head() metadata (title, description, Open Graph,
 * Twitter, canonical) plus Service + LocalBusiness + BreadcrumbList JSON-LD,
 * so every /services/* page ships full SEO/AEO/GEO data in the SSR HTML.
 */

import { brand } from "@/config/brand";
const SITE_URL = brand.url;
const OG_IMAGE = `${SITE_URL}/__l5e/assets-v1/aabeccab-688e-44b7-96b1-aaf70d6a2a8d/social-share-cover.png`;
const OG_ALT = "Digitally Baffled LTD — UK Digital Transformation Consultancy";

export interface ServiceSeo {
  title: string;
  tagline?: string;
  metaDesc: string;
  slug: string;
  whatWeDoItems: { heading: string; body: string }[];
}

export function buildServiceHead({ title, metaDesc, slug, whatWeDoItems }: ServiceSeo) {
  const canonicalUrl = `${SITE_URL}/services/${slug}`;
  const fullTitle = `${title} | Digitally Baffled`;

  const breadcrumbJsonLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
      { "@type": "ListItem", position: 2, name: "Services", item: `${SITE_URL}/services` },
      { "@type": "ListItem", position: 3, name: title, item: canonicalUrl },
    ],
  };

  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Service",
        "@id": `${canonicalUrl}#service`,
        name: title,
        description: metaDesc,
        url: canonicalUrl,
        serviceType: title,
        areaServed: [
          { "@type": "Country", name: "United Kingdom" },
          { "@type": "AdministrativeArea", name: "Hampshire" },
          { "@type": "City", name: "Ringwood" },
        ],
        provider: { "@id": `${SITE_URL}/#localbusiness` },
        hasOfferCatalog: {
          "@type": "OfferCatalog",
          name: `${title} — What's Included`,
          itemListElement: whatWeDoItems.map((item, i) => ({
            "@type": "Offer",
            position: i + 1,
            name: item.heading,
            description: item.body,
          })),
        },
      },
      {
        "@type": ["LocalBusiness", "ProfessionalService"],
        "@id": `${SITE_URL}/#localbusiness`,
        name: "Digitally Baffled LTD",
        url: SITE_URL,
        logo: `${SITE_URL}/__l5e/assets-v1/2b67c319-0b6a-42bb-83d5-75847a4a9ca2/logo-horizontal.png`,
        image: OG_IMAGE,
        description:
          "UK digital transformation consultancy helping small businesses, tradespeople, and startups go digital-first. Web design, SEO, AEO & GEO, PPC & Google Ads, and ongoing support.",
        address: {
          "@type": "PostalAddress",
          streetAddress: "71–75 Shelton Street",
          addressLocality: "London",
          addressRegion: "England",
          postalCode: "WC2H 9JQ",
          addressCountry: "GB",
        },
        geo: { "@type": "GeoCoordinates", latitude: 51.5152, longitude: -0.1267 },
        telephone: "+447511683542",
        email: "hello@digitallybaffled.com",
        priceRange: "££",
        currenciesAccepted: "GBP",
        paymentAccepted: "Bank Transfer, Credit Card",
        areaServed: [
          { "@type": "Country", name: "United Kingdom" },
          { "@type": "AdministrativeArea", name: "Hampshire" },
          { "@type": "City", name: "Ringwood" },
        ],
        sameAs: [
          "https://www.linkedin.com/company/digitally-baffled",
          "https://www.instagram.com/digitallybaffled",
          "https://www.facebook.com/digitallybaffled",
        ],
        contactPoint: {
          "@type": "ContactPoint",
          telephone: "+447511683542",
          contactType: "customer service",
          availableLanguage: "English",
          contactOption: "TollFree",
        },
        founder: {
          "@type": "Person",
          "@id": `${SITE_URL}/#morgan-antell`,
          name: "Morgan Antell",
        },
        isPartOf: { "@id": `${SITE_URL}/#website` },
      },
    ],
  };

  return {
    meta: [
      { title: fullTitle },
      { name: "description", content: metaDesc },
      { property: "og:site_name", content: "Digitally Baffled" },
      { property: "og:title", content: fullTitle },
      { property: "og:description", content: metaDesc },
      { property: "og:url", content: canonicalUrl },
      { property: "og:type", content: "website" },
      { property: "og:image", content: OG_IMAGE },
      { property: "og:image:secure_url", content: OG_IMAGE },
      { property: "og:image:type", content: "image/png" },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { property: "og:image:alt", content: OG_ALT },
      { property: "og:locale", content: "en_GB" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:site", content: "@digitallybaffld" },
      { name: "twitter:creator", content: "@digitallybaffld" },
      { name: "twitter:title", content: fullTitle },
      { name: "twitter:description", content: metaDesc },
      { name: "twitter:image", content: OG_IMAGE },
      { name: "twitter:image:alt", content: OG_ALT },
    ],
    links: [{ rel: "canonical", href: canonicalUrl }],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(jsonLd) },
      { type: "application/ld+json", children: JSON.stringify(breadcrumbJsonLd) },
    ],
  };
}
